import React, { useState, useEffect } from "react";
import { ReactComponent as IconAlert } from "../../../assets/svg/icon-alert.svg";
import { useTranslation } from "react-i18next";
import { Password } from "primereact/password";
import useToast from "../../common/toast/useToast";
import useErrorsTranslation from "../../../hooks/translations/useErrorsTranslation";
import { resetPassword, verifyResetCode, forgotPassword } from "../../../useCase/userUseCase";
import StandardDialog from "../../common/standardDialog";

const emailRegex = /^[^\s@]+@[^\s@]+(\.[^\s@]+)+$/;

function ResetPasswordModalContent({ isModalOpen, onClose, initialEmail = "" }) {
  const { t } = useTranslation("commonTranslation");
  const { showSuccessToast } = useToast();
  const { getErrorMessage } = useErrorsTranslation();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [isSuccess, setIsSuccess] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const handleOnClose = () => {
    onClose();
    setEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
    setResendCooldown(60);
    setStep(1);
    setLoading(false);
    setIsSuccess(true);
    setErrorMessage("");
  };

  useEffect(() => {
    setIsSuccess(true);
    setErrorMessage("");
  }, [email, resetCode]);

  useEffect(() => {
    if (step === 2 && resendCooldown > 0) {
      const timerId = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    }
  }, [step, resendCooldown]);

  // Reseta erros quando muda de etapa
  useEffect(() => {
    setIsSuccess(true);
    setErrorMessage("");
  }, [step]);

  useEffect(() => {
    if (!isModalOpen) return;
    setEmail(String(initialEmail || "").trim());
  }, [isModalOpen, initialEmail]);

  const supportContactLink = `https://linkedscope.com/contact?cname=&email=${encodeURIComponent(
    email || ""
  )}&company=`;
  const supportLinkText = t("auth.supportLink");
  const connectedSupportLinkText = supportLinkText
    ? `${supportLinkText.charAt(0).toLowerCase()}${supportLinkText.slice(1)}`
    : supportLinkText;

  const getGenericFallbackMessage = () => (
    <span>
      {t("auth.genericError")}{" "}
      <a href={supportContactLink} target="_blank" rel="noopener noreferrer">
        {connectedSupportLinkText}
      </a>
    </span>
  );

  const resolveResetErrorMessage = (error) => {
    const errorCode = error?.code;
    if (errorCode === "error.400.user_not_confirmed") {
      return t("auth.login.userNotConfirmed");
    }
    if (errorCode === "error.400.user_blocked") {
      return (
        <span>
          {t("auth.login.userBlocked")}{" "}
          <a href={supportContactLink} target="_blank" rel="noopener noreferrer">
            {supportLinkText}
          </a>
        </span>
      );
    }
    return errorCode ? getErrorMessage(errorCode) : getGenericFallbackMessage();
  };

  const handleSendCode = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setIsSuccess(false);
      setErrorMessage(t("toastMessages.validation.requiredEmail"));
      return;
    }
    if (!emailRegex.test(normalizedEmail)) {
      setIsSuccess(false);
      setErrorMessage(t("toastMessages.validation.invalidEmail"));
      return;
    }
    try {
      setLoading(true);
      await forgotPassword({ email: normalizedEmail });
      setEmail(normalizedEmail);
      setIsSuccess(true);
      setErrorMessage("");
      setStep(2);
    } catch (error) {
      setIsSuccess(false);
      const errorCode = error?.code;
      const isEmailFormatValid = emailRegex.test(normalizedEmail);
      const isInvalidEmailFromApi = errorCode === "error.400.invalid_email";
      const isUserNotFound = errorCode === "error.400.user_not_found";

      if (isEmailFormatValid && (isInvalidEmailFromApi || isUserNotFound)) {
        setErrorMessage(t("auth.login.userNotFound"));
        return;
      }

      setErrorMessage(resolveResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    try {
      setLoading(true);
      await forgotPassword({ email });
      setIsSuccess(true);
      setErrorMessage("");
      setResendCooldown(60);
    } catch (error) {
      setIsSuccess(false);
      setErrorMessage(resolveResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode || resetCode.length !== 7) {
      setIsSuccess(false);
      setErrorMessage(t("auth.resetPassword.invalidCodeLength"));
      return;
    }
    try {
      setLoading(true);
      await verifyResetCode({ email, code: resetCode });
      setIsSuccess(true);
      setErrorMessage("");
      setStep(3);
    } catch (error) {
      setIsSuccess(false);
      setErrorMessage(resolveResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const validation = isPasswordValid(newPassword, confirmPassword);

    if (!newPassword || !confirmPassword) {
      setIsSuccess(false);
      setErrorMessage(t("toastMessages.validation.requiredPassword"));
      return;
    }

    if (!validation.isValid) {
      setIsSuccess(false);
      setErrorMessage(validation.message);
      return;
    }

    try {
      setLoading(true);
      await resetPassword({ email, code: resetCode, password: newPassword });
      setIsSuccess(true);
      setErrorMessage("");
      showSuccessToast(t("auth.resetPassword.resetPasswordSuccess"));
      handleOnClose();
    } catch (error) {
      setIsSuccess(false);
      setErrorMessage(resolveResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  function isPasswordValid(password, confirmPassword) {
    const returnValue = { isValid: false, message: "" };

    // Verifica se as senhas são iguais
    if (password !== confirmPassword) {
      returnValue.message = t("toastMessages.validation.passwordMismatch");
      return returnValue;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(password)) {
      returnValue.message = t("toastMessages.validation.invalidPassword");
      return returnValue;
    }

    returnValue.isValid = true;
    returnValue.message = "ok";

    return returnValue;
  }

  const resolveHeaderText = () => {
    if (step === 1) return t("auth.resetPassword.title");
    if (step === 2) return t("auth.resetPassword.code");
    return t("auth.resetPassword.resetPassword");
  };

  return (
    <StandardDialog
      visible={isModalOpen}
      onHide={handleOnClose}
      className="reset-password-dialog"
      header={<h2>{resolveHeaderText()}</h2>}
    >
      {step === 1 && (
        <form
          className="modal-content"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendCode();
          }}
          noValidate
        >
          <p>{t("auth.resetPassword.description")}</p>

          {!isSuccess && (
            <div className="message msg-error">
              <IconAlert /> {errorMessage}
            </div>
          )}

          <div className="modal-form">
            <input
              type="email"
              className="ls-input"
              disabled={loading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("settings.profile.fields.email")}
            />
          </div>

          <div className="modal-buttons">
            <button
              className={`ls-btn ls-btn-big ls-btn-pink ${!email || loading ? "ls-btn-disabled" : ""}`}
              disabled={!email || loading}
              type="submit"
            >
              {loading ? <div className="spinner"></div> : t("auth.resetPassword.sendCodeButton")}
            </button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form
          className="modal-content"
          onSubmit={(e) => {
            e.preventDefault();
            handleVerifyCode();
          }}
          noValidate
        >
          <p>{t("auth.resetPassword.codeDescription")}</p>

          {!isSuccess && (
            <div className="message msg-error">
              <IconAlert /> {errorMessage}
            </div>
          )}

          <div className="modal-form">
            <input
              type="text"
              className="ls-input"
              disabled={loading}
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.slice(0, 7))}
              placeholder={t("auth.resetPassword.codePlaceholder")}
              maxLength={7}
            />
          </div>

          <div className="message msg-info">
            {resendCooldown > 0 ? (
              <strong>
                {t("auth.resetPassword.codeTimer", {
                  count: String(resendCooldown).padStart(2, "0"),
                })}
              </strong>
            ) : (
              <a
                href="#"
                className={loading ? "disabled-link" : ""}
                aria-disabled={loading}
                onClick={(e) => {
                  e.preventDefault();
                  if (loading) return;
                  handleResendCode();
                }}
              >
                {t("auth.resetPassword.resendCodeButton")}
              </a>
            )}
            {t("auth.resetPassword.codeExpire")}
          </div>

          <div className="modal-buttons">
            <button
              className={`ls-btn ls-btn-big ls-btn-pink ${resetCode.length !== 7 || loading ? "ls-btn-disabled" : ""}`}
              disabled={resetCode.length !== 7 || loading}
              type="submit"
            >
              {loading ? <div className="spinner"></div> : t("auth.resetPassword.continueButton")}
            </button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form
          className="modal-content"
          onSubmit={(e) => {
            e.preventDefault();
            handleResetPassword();
          }}
          noValidate
        >
          {!isSuccess && (
            <div className="message msg-error">
              <IconAlert /> {errorMessage}
            </div>
          )}

          <div className="modal-form">
            <label>
              {t("auth.resetPassword.resetPasswordPlaceholder")}
              <Password
                value={newPassword}
                disabled={loading}
                onChange={(e) => setNewPassword(e.target.value)}
                feedback={true}
                promptLabel={t("fields.passwordStatus.prompt")}
                weakLabel={t("fields.passwordStatus.weak")}
                mediumLabel={t("fields.passwordStatus.medium")}
                strongLabel={t("fields.passwordStatus.strong")}
                toggleMask
                autoComplete="new-password"
                inputProps={{
                  autoComplete: "new-password",
                }}
              />
            </label>

            <label>
              {t("auth.resetPassword.resetPasswordConfirmPlaceholder")}
              <Password
                value={confirmPassword}
                disabled={loading}
                onChange={(e) => setConfirmPassword(e.target.value)}
                feedback={false}
                toggleMask
                autoComplete="new-password"
                inputProps={{
                  autoComplete: "new-password",
                }}
              />
            </label>
          </div>

          <div className="modal-buttons">
            <button
              className={`ls-btn ls-btn-big ls-btn-pink ${!newPassword || !confirmPassword || loading ? "ls-btn-disabled" : ""}`}
              disabled={!newPassword || !confirmPassword || loading}
              type="submit"
            >
              {loading ? <div className="spinner"></div> : t("auth.resetPassword.resetPassword")}
            </button>
          </div>
        </form>
      )}
    </StandardDialog>
  );
}

const ResetPasswordModal = ({ isModalOpen, onClose, initialEmail = "" }) => {
  return (
    <ResetPasswordModalContent
      isModalOpen={isModalOpen}
      onClose={onClose}
      initialEmail={initialEmail}
    />
  );
};

export default ResetPasswordModal;
